import { useSessionPermission } from '@btime/react-session'
import { useFilter } from '@btime/react-contexts'
import { concatIf } from '@btime/helpers'
import { COLORS } from '@btime/constants'
import React from 'react'

import { useProcessesListById } from '../_load/ProcessesListById'
import { Col, Grid } from '../../../packages/kiti/components/structure/Grid'
import { Title } from '../../../packages/kiti/components/presentation/Text'
import Separator from '../../../packages/kiti/components/helpers/Separator'
import { HorizontalLine } from '../../../components/shapes/Line'
import { useNavigation } from '../../../utils/contexts/NavigationContext'
import {
  useOpenEditFieldsModal,
  useOpenExportHistoryModal,
  useOpenExportModal,
  useOpenImportHistoryModal,
  useOpenPublicTokensModal,
  useOpenRoutinesModal,
  useOpenSemaphoreConfigModal,
  useOpenServiceOrderAutomaticDistributionListModal,
  useOpenTemplatesModal,
  useOpenTriggersModal
} from '../../../utils/routes/modals/modalsActions'
import { useTypeTranslation } from '../../places/_context/PlaceTypeContext'
import DropdownLink from '../../../components/navigation/DropdownLink'
import IconButton from '../../../components/actions/IconButton'
import TabMenuBar from './TabMenuBar'

const PageHeader = () => {
  const { t } = useTypeTranslation()
  const navigate = useNavigation()
  const screen = useProcessesListById()
  const filter = useFilter()

  const openTriggersModal = useOpenTriggersModal()
  const openEditFieldsModal = useOpenEditFieldsModal()
  const openImportHistoryModal = useOpenImportHistoryModal()
  const openExportModal = useOpenExportModal()
  const openExportHistoryModal = useOpenExportHistoryModal()
  const openTemplatesModal = useOpenTemplatesModal()
  const openSemaphoreConfigModal = useOpenSemaphoreConfigModal()
  const openRoutinesModal = useOpenRoutinesModal()
  const openServiceOrderAutomaticDistributionListModal = useOpenServiceOrderAutomaticDistributionListModal()
  const openPublicTokensModal = useOpenPublicTokensModal()

  const canListTriggers = useSessionPermission('listTrigger')
  const canListRoutines = useSessionPermission('listRoutine')
  const canExport = useSessionPermission('exportServiceOrder')
  const canImport = useSessionPermission('importServiceOrder')
  const canSetup = useSessionPermission('setupServiceOrder')
  const canListTemplate = useSessionPermission('listTemplate')
  const canListPublicToken = useSessionPermission('listPublicToken')
  const canUseAutomaticDistribution = useSessionPermission('useAutomaticDistribution')

  const options = [
    ...concatIf(canListRoutines, [
      {
        value: 'routines',
        iconLeft: 'RiCalendar2Line',
        onClick: () => openRoutinesModal({ entities: ['service_order'] })
      },
      { line: true }
    ]),

    ...concatIf(canListTemplate, [
      {
        value: 'templates',
        iconLeft: 'RiFileCopy2Line',
        onClick: () => openTemplatesModal('service_order')
      },

      { line: true }
    ]),

    ...concatIf(canImport, [
      {
        value: 'import',
        iconLeft: 'RiUploadCloud2Line',
        onClick: () => navigate(`/import/v2/serviceOrder`)
      },
      {
        value: 'importHistory',
        iconLeft: 'RiTimeLine',
        onClick: () => openImportHistoryModal('serviceOrder')
      },

      { line: true }
    ]),

    ...concatIf(canExport, [
      {
        value: 'export',
        iconLeft: 'RiDownloadCloud2Line',
        onClick: () => openExportModal({ entity: 'serviceOrder', filter })
      },

      {
        value: 'exportHistory',
        iconLeft: 'RiTimeLine',
        onClick: () => openExportHistoryModal('serviceOrder')
      },
      { line: true }
    ]),

    ...concatIf(canListPublicToken, [
      {
        value: 'publicToken',
        iconLeft: 'BsReply',
        onClick: () => openPublicTokensModal('service_order')
      },

      { line: true }
    ]),

    ...concatIf(canSetup, [
      {
        value: 'semaphore',
        iconLeft: 'RiAlarmWarningLine',
        onClick: () => openSemaphoreConfigModal({ name: 'service_order_started_since_colors' })
      },
      {
        value: 'fields',
        iconLeft: 'RiListSettingsLine',
        onClick: () => openEditFieldsModal('service_orders')
      },

      {
        value: 'unproductives',
        iconLeft: 'RiListSettingsLine',
        onClick: () => navigate('/minicruds/unproductive')
      },
      {
        value: 'pendencies',
        iconLeft: 'RiListSettingsLine',
        onClick: () => navigate('/minicruds/pending')
      }
    ]),
    ...concatIf(canUseAutomaticDistribution, [
      {
        value: 'configurations.automatic.distribution',
        iconLeft: 'RiUserSettingsLine',
        onClick: () => openServiceOrderAutomaticDistributionListModal({ name: 'automatic_service_orders_distribution' })
      }
    ])
  ]

  return (
    <>
      <Grid justify="space-between" align="center" gutter={1}>
        <Col>
          <Title size="h2" color={COLORS.dark3} noWrap>
            {screen?.name}
          </Title>
        </Col>

        <Col flex={1}></Col>

        {!!options.length && (
          <Grid align="center" gap={0.5}>
            {canListTriggers && (
              <>
                <IconButton
                  iconLeft="RiRobotLine"
                  label={t('listServiceOrders:menu.triggers')}
                  transparent
                  noWrap
                  onClick={() =>
                    openTriggersModal({
                      entities: [
                        'service_order',
                        'event',
                        'reply',
                        'service_order_reply',
                        'service_order_tag_assigned',
                        'service_order_tag_removed'
                      ]
                    })
                  }
                />

                <IconButton
                  iconLeft="RiPlugLine"
                  label={t('listServiceOrders:menu.integrations')}
                  transparent
                  noWrap
                  onClick={() =>
                    openTriggersModal({ entities: ['service_order', 'event', 'reply'], integrationsOnly: true })
                  }
                />
              </>
            )}

            <DropdownLink
              fromRight
              topDistance={5}
              t={key => t(`listServiceOrders:menu.${key}`)}
              options={options}
              actionContent={
                <IconButton label={t('moreOptions')} iconRight="BsThreeDotsVertical" noWrap size="small" transparent />
              }
            />
          </Grid>
        )}
      </Grid>
      <Separator weight={2} />
      <Col>
        <TabMenuBar screen={screen} />
        <HorizontalLine thin lineColor={COLORS.light2} />
      </Col>
      <Separator weight={4} />
    </>
  )
}

export default PageHeader
